import fb from '~/api/core/facebook';
import { createRgFormAPI } from '~/api/core/rgRequest';

import { Subscription, FacebookSubscription } from '~/api/models/leadads/subscription';

type FacebookConnections = {
  fb_page_subscriptions: FacebookSubscription[];
  domain_has_subscription: boolean;
};

export type Connections = {
  hasSubscription: boolean,
  id: string | null,
  leadType: string,
};

const getLeadAdConnectionsAPI = createRgFormAPI('/get_lead_ads_connections');
const setupLeadAdsAPI = createRgFormAPI('/set_up_lead_ads');
const deleteLeadRouterConnectionsAPI = createRgFormAPI('/delete_lr_connection');

/**
 * Will retrieve the lead router subscription. If none exists, the function will return an object where the id and leadType are undefined but hasSubscription will be 'false'
 */
export const getSubscription = async (pageId: string, accountId: string): Promise<Connections> => {
  const { data } = await getLeadAdConnectionsAPI.post({ pageId, accountId });

  const {
    fb_page_subscriptions: subscriptions,
    domain_has_subscription,
  }: FacebookConnections = data;

  const [subscription] = subscriptions;

  return {
    hasSubscription: subscription && domain_has_subscription,
    id: subscription?.id,
    leadType: subscription?.lead_type,
  };
};

export const getPageAccessToken = (pageId: string): Promise<{ access_token: string }> => fb(`/${pageId}?fields=access_token`, 'get');

/**
 * Will create a subscription for a lead type. This will automatically retrieve the page access for you given the page id
 */
export const createSubscription = async (subscription: Omit<Subscription, 'id'>): Promise<FacebookSubscription> => {
  const {
    userId,
    accountId,
    pageInfo,
    creatorId,
    leadType,
  } = subscription;

  const { access_token } = await getPageAccessToken(pageInfo.id);

  const facebookSubscription = {
    facebook_page_id: pageInfo.id,
    facebook_page_title: pageInfo.name,
    facebook_page_access_token: access_token,
    facebook_user_id: userId,
    lead_type: leadType,
    // NOTE: There may be a better way to retrieve the creator id so that we don't have to pass it as an arg to the  machine
    creator_id: creatorId,
  };

  const { data }: { data: FacebookSubscription } = await setupLeadAdsAPI.post({
    subscription: facebookSubscription,
    pageId: pageInfo.id,
    accountId,
  });

  return data;
};

/**
 * Will reset a subscription for a lead type. This will automatically retrieve the page access for you given the page id
 */
export const resetSubscription = async (subscription: Subscription): Promise<FacebookSubscription> => {
  const { id } = subscription;

  await deleteLeadRouterConnectionsAPI.post({ subscriptionId: id });

  return createSubscription(subscription);
};

export const checkTosAccepted = (pageId: string): Promise<boolean> => fb(`/${pageId}?fields=leadgen_tos_accepted`, 'get')
  .then(({ leadgen_tos_accepted }) => leadgen_tos_accepted);

export async function checkSubscribedApps(pageId: string): Promise<void> {
  const { access_token } = await getPageAccessToken(pageId);

  return fb(
    `/${pageId}/subscribed_apps`,
    'get',
    {
      access_token,
      subscribed_fields: 'leadgen',
    },
  ).then((subscriptions) => {
    if (subscriptions.data.length === 0) {
      return subscribe(pageId, access_token);
    }
  });
}

function subscribe(page: string, token: string) {
  return fb(
    `/${page}/subscribed_apps`,
    'post',
    {
      access_token: token,
      subscribed_fields: 'leadgen',
    },
  );
}
